import React, { useState, useEffect } from "react";
import { useSidebar } from "../context/SidebarContext";
import { useToki } from "../context/TokiContext";
import { useSuperApp } from "../hooks/useSuperApp";
import { RxDoubleArrowRight } from "react-icons/rx";
import { HiOutlineDocumentChartBar } from "react-icons/hi2";
import { FiArrowUpRight, FiUser, FiBell, FiPlus, FiArrowLeft, FiClock, FiX, FiFilter, FiCheck } from "react-icons/fi";
import { useNavigate, useSearchParams, useLocation } from "react-router-dom";
import { useTranslation } from "react-i18next";
import LoginModal from "../components/LoginModal";
import { usePostAction } from "../hooks/usePostAction";
import { addRecentSearch, clearRecentSearches, getRecentSearches, removeRecentSearch } from "../utils/recentSearches";
import { useJobs } from "../hooks/useJobs";
import { useRegion } from "../context/RegionContext";
import { useNotifications } from "../hooks/useNotifications";
import { useSearchFilter } from "../context/SearchFilterContext";
import { isDeskHeader, DESK_SURFACE_CLASS } from "../utils/pageSurface";
// Imported statically on purpose: lazy-loading these took framer-motion out of main, and webpack
// then copied its AnimatePresence into every lazy chunk (+30 KB gz, over check-size's 600 KB gate)
import PostJobModal from "../components/PostJobModal";
import NotificationPanel from "../components/NotificationPanel";
import ProfileMenu from "../components/ProfileMenu";

function ExpandingSearch() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const location = useLocation();
  const [searchParams] = useSearchParams();
  const region = useRegion();
  const mobileInputRef = React.useRef(null);

  // Desktop: the bar drops a panel while it has focus — the same two lists /search shows
  // on a phone (recent queries on an empty field, matching job titles once something is
  // typed). Desktop had neither: you typed blind and waited for the feed behind the bar
  // to change under you.
  const [panelOpen, setPanelOpen] = React.useState(false);
  const desktopRef = React.useRef(null);
  const [recent, setRecent] = React.useState(() => getRecentSearches());

  // Mobile bar tap: land on the dedicated search page and focus the input — a plain,
  // instant navigation like any other link, no custom transition. The bar itself stays
  // in the header (mounted across routes), so it keeps being the one editable field
  // whether you're on it before or after the tap.
  const goSearch = () => {
    if (location.pathname !== '/search') {
      navigate('/search');
    }
    mobileInputRef.current?.focus();
  };

  // Get initial search value from URL
  const urlSearchQuery = searchParams.get('q') || '';
  const [inputValue, setInputValue] = useState(urlSearchQuery);

  // Sync input with URL when URL changes (e.g., browser back/forward)
  useEffect(() => {
    setInputValue(urlSearchQuery);
  }, [urlSearchQuery]);

  // Debounced navigation to URL with search query
  useEffect(() => {
    const timer = setTimeout(() => {
      const trimmedValue = inputValue.trim();
      const onHome = location.pathname === '/' || location.pathname === '';
      const onSearchPage = location.pathname === '/search';

      // Already on a page that reads `q` itself: update it in place rather than
      // bouncing away from wherever the admin already is.
      if (onHome || onSearchPage) {
        if (trimmedValue) {
          const newParams = new URLSearchParams(searchParams);
          newParams.set('q', trimmedValue);
          navigate(`${location.pathname}?${newParams.toString()}`, { replace: true });
        } else if (urlSearchQuery) {
          const newParams = new URLSearchParams(searchParams);
          newParams.delete('q');
          const paramString = newParams.toString();
          navigate(paramString ? `${location.pathname}?${paramString}` : location.pathname, { replace: true });
        }
      } else if (trimmedValue) {
        // Elsewhere: mobile's own affordance is the dedicated search page; desktop's
        // hover-search has always landed typed queries on the home feed instead.
        // 1024, not 768: tailwind.config.js pushes `md` out to 1024, so this is the line
        // every md:/lg: class in the header switches on.
        const isDesktop = window.innerWidth >= 1024;
        navigate(isDesktop ? `/?q=${encodeURIComponent(trimmedValue)}` : `/search?q=${encodeURIComponent(trimmedValue)}`);
      }
    }, 300); // 300ms debounce

    return () => clearTimeout(timer);
  }, [inputValue, location.pathname, navigate, searchParams, urlSearchQuery]);

  const handleInputChange = (e) => {
    setInputValue(e.target.value);
    setPanelOpen(true);
  };

  // History is written by other surfaces too (/search, Enter here), so it is re-read when
  // the panel opens rather than trusted from mount.
  useEffect(() => {
    if (panelOpen) setRecent(getRecentSearches());
  }, [panelOpen]);

  // Clicking anywhere else closes it. mousedown rather than click so a panel row that
  // navigates does not have to survive the rest of the gesture.
  useEffect(() => {
    if (!panelOpen) return undefined;
    const onDown = (e) => {
      if (desktopRef.current && !desktopRef.current.contains(e.target)) setPanelOpen(false);
    };
    document.addEventListener("mousedown", onDown);
    return () => document.removeEventListener("mousedown", onDown);
  }, [panelOpen]);

  // The panel's own query, a beat behind the field: the bar already navigates on a 300ms
  // debounce, and asking the server per keystroke on top of that is the same list twice.
  const [suggestQuery, setSuggestQuery] = useState("");
  useEffect(() => {
    const timer = setTimeout(() => setSuggestQuery(inputValue.trim()), 250);
    return () => clearTimeout(timer);
  }, [inputValue]);

  const suggestFilters = React.useMemo(
    () => ({ searchQuery: suggestQuery, country: region.jobCountryFilter }),
    [suggestQuery, region.jobCountryFilter]
  );
  // Only while the panel is open and there is something to match — the header is mounted
  // on every page, and this must not become a fetch that follows people around.
  const { jobs: suggestJobs } = useJobs(1, 8, suggestFilters, {
    enabled: panelOpen && suggestQuery.length > 1,
  });

  // Distinct posting titles the text actually matched, the way /search derives its own
  // (Search.jsx) — the query itself is not repeated back as a suggestion.
  const titleSuggestions = React.useMemo(() => {
    if (suggestQuery.length < 2) return [];
    const q = suggestQuery.toLowerCase();
    const seen = new Set();
    const out = [];
    for (const job of suggestJobs || []) {
      const name = (job.name || "").trim();
      if (!name || name.toLowerCase() === q) continue;
      if (seen.has(name.toLowerCase())) continue;
      seen.add(name.toLowerCase());
      out.push(name);
      if (out.length >= 6) break;
    }
    return out;
  }, [suggestQuery, suggestJobs]);

  // A picked row is a finished search: it goes into the history and lands on the feed,
  // the same as pressing Enter.
  const pickSuggestion = (term) => {
    setInputValue(term);
    setRecent(addRecentSearch(term));
    setPanelOpen(false);
    navigate(`/?q=${encodeURIComponent(term)}`);
  };

  // Clears the field in place — same empty-query handling the debounce effect
  // already does, just fired immediately instead of waiting 300ms.
  const clearInput = (e) => {
    e.stopPropagation();
    setInputValue('');
    mobileInputRef.current?.focus();
  };

  const handleKeyDown = (e) => {
    if (e.key === 'Escape') {
      setPanelOpen(false);
      return;
    }
    if (e.key === 'Enter') {
      setPanelOpen(false);
      e.preventDefault();
      // Immediate navigation on Enter
      const trimmedValue = inputValue.trim();
      const isDesktop = window.innerWidth >= 1024; // same `md` line as the effect above
      if (trimmedValue) {
        addRecentSearch(trimmedValue);
        navigate(isDesktop ? `/?q=${encodeURIComponent(trimmedValue)}` : `/search?q=${encodeURIComponent(trimmedValue)}`);
      } else {
        navigate(isDesktop ? '/' : '/search');
      }
    }
  };

  // Focus alone is not enough to go card-shaped: with no history and nothing typed there
  // is no panel to be the card, and a transparent bar over the white header read as the
  // field having disappeared.
  const showPanel = panelOpen && (recent.length > 0 || titleSuggestions.length > 0);

  return (
    <div className="flex items-center flex-1 md:flex-none min-w-0 mx-2 md:mx-0" data-search-bar>
      {/* Mobile search bar — pill styled via theme vars (light/dark), same editable
          input pattern as before. Tapping it (when elsewhere) is a plain, instant
          navigation to /search — the bar stays mounted in the header across that route
          change, so it keeps being the one place typing happens, on every page. */}
      {/* display comes from classes so md:hidden can win at desktop widths */}
      <div
        className="md:hidden flex items-center"
        onClick={goSearch}
        style={{
          flex: 1,
          minWidth: 0,
          gap: 9,
          background: 'var(--card2, rgba(255,255,255,.07))',
          border: '1px solid var(--border, rgba(255,255,255,.08))',
          borderRadius: 999,
          padding: '11px 15px',
          cursor: 'pointer',
        }}
      >
        <div className="flex items-center justify-center shrink-0">
          <svg
            width="18"
            height="18"
            viewBox="0 0 24 24"
            fill="none"
            stroke="#94A3B8"
            strokeWidth={2}
          >
            <circle cx="11" cy="11" r="7" />
            <path d="m20 20-3.5-3.5" />
          </svg>
        </div>
        <input
          ref={mobileInputRef}
          type="text"
          autoComplete="off"
          autoCorrect="off"
          autoCapitalize="off"
          spellCheck="false"
          placeholder={t("header.search")}
          value={inputValue}
          onChange={handleInputChange}
          onKeyDown={handleKeyDown}
          className="header-search-input flex-1 bg-transparent outline-none border-none text-[#020618] px-0 text-left font-medium min-w-0 placeholder:text-[#94A3B8] placeholder:font-medium"
          style={{ WebkitAppearance: 'none' }}
        />
        {inputValue && (
          <button
            type="button"
            onClick={clearInput}
            aria-label="clear"
            className="flex items-center justify-center shrink-0 w-[18px] h-[18px] rounded-full"
            style={{ background: 'var(--text2, #94A3B8)' }}
          >
            <svg width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="var(--card, #fff)" strokeWidth={3} strokeLinecap="round">
              <path d="M18 6 6 18M6 6l12 12" />
            </svg>
          </button>
        )}
      </div>

      {/* Desktop: expanding hover search, plus the suggestion panel it drops on focus. */}
      <div className="relative group w-full md:w-auto hidden md:block" ref={desktopRef}>
        {/* Focused counts as hovered for the width: the bar should not shrink back under
            the panel the moment the pointer leaves it. Open it goes wider still — the
            panel below it is a list of full job titles, not a word. `relative z-10` keeps
            it over the panel, which is what hides the panel's top edge and shadow and
            makes the two read as one surface. */}
        <div className={`relative z-10 flex items-center border border-transparent rounded-full transition-all duration-300 h-11 px-1 ${showPanel ? "bg-transparent" : "bg-[#EEF1F8]"} ${panelOpen ? "w-[36rem] xl:w-[44rem]" : "w-[30rem] xl:w-[36rem] group-hover:w-[34rem] xl:group-hover:w-[42rem]"}`}>
          <input
            type="text"
            autoComplete="off"
            autoCorrect="off"
            autoCapitalize="off"
            spellCheck="false"
            placeholder={t("header.search")}
            value={inputValue}
            onChange={handleInputChange}
            onKeyDown={handleKeyDown}
            onFocus={() => setPanelOpen(true)}
            className="flex-1 bg-transparent outline-none border-none text-[#020618] px-3 text-center text-base font-normal min-w-0 placeholder:text-[#94A3B8] placeholder:font-medium"
            style={{ WebkitAppearance: 'none' }}
          />
          {inputValue && (
            <button
              type="button"
              onClick={clearInput}
              aria-label="clear"
              className="flex items-center justify-center shrink-0 w-5 h-5 rounded-full mr-1"
              style={{ background: '#CAD5E2' }}
            >
              <svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="#fff" strokeWidth={3} strokeLinecap="round">
                <path d="M18 6 6 18M6 6l12 12" />
              </svg>
            </button>
          )}
          {/* Desktop: trailing icon in white circle (expanding hover design) */}
          <div className="flex items-center justify-center w-9 h-9 bg-white rounded-full shrink-0">
            <svg
              className="w-5 h-5 text-[#020618]"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z"
              />
            </svg>
          </div>
        </div>

        {/* One card, not a bar with a card under it: the panel starts at the bar's own top
            and reserves its height as padding, so the field sits inside it (the bar goes
            transparent above, z-10) and a single outline runs around the whole thing. */}
        {showPanel && (
          <div
            className="absolute left-0 right-0 top-0 z-0 rounded-[22px] pt-[50px] pb-2 overflow-hidden"
            style={{
              background: "var(--card, #F8FAFC)",
              border: "1px solid var(--border2, #CBD5E1)",
              boxShadow: "0 18px 40px rgba(15,23,42,0.12)",
            }}
          >
            {/* Nothing typed yet -> what was searched before. */}
            {titleSuggestions.length === 0 ? (
              <>
                <div className="flex items-center justify-between px-5 pt-1 pb-1.5">
                  <span className="font-extrabold text-[12.5px]" style={{ color: "var(--text2, #94A3B8)" }}>
                    {t("search.recent", "Сүүлийн")}
                  </span>
                  <button
                    type="button"
                    onClick={() => setRecent(clearRecentSearches())}
                    className="text-[12px] font-bold"
                    style={{ color: "var(--text2, #94A3B8)" }}
                  >
                    {t("search.clearAll", "Цэвэрлэх")}
                  </button>
                </div>
                {recent.map((term) => (
                  <button
                    key={term}
                    type="button"
                    onClick={() => pickSuggestion(term)}
                    className="w-full flex items-center gap-3 text-left px-5 py-2 hover:bg-white/70 transition-colors"
                  >
                    <span
                      className="flex items-center justify-center shrink-0 w-8 h-8 rounded-full"
                      style={{ background: "var(--card, #fff)" }}
                    >
                      <FiClock className="text-[14px]" style={{ color: "var(--text2, #94A3B8)" }} />
                    </span>
                    <span className="flex-1 min-w-0 truncate font-bold text-[13.5px]" style={{ color: "var(--text, #020618)" }}>
                      {term}
                    </span>
                    <span
                      role="button"
                      aria-label="remove"
                      onClick={(e) => { e.stopPropagation(); setRecent(removeRecentSearch(term)); }}
                      className="flex items-center justify-center shrink-0 w-7 h-7"
                    >
                      <FiX className="text-[15px]" style={{ color: "var(--text2, #94A3B8)" }} />
                    </span>
                  </button>
                ))}
              </>
            ) : (
              titleSuggestions.map((title) => (
                <button
                  key={title}
                  type="button"
                  onClick={() => pickSuggestion(title)}
                  className="w-full flex items-center gap-3 text-left px-5 py-2 hover:bg-white/70 transition-colors"
                >
                  <span className="flex items-center justify-center shrink-0 w-8 h-8">
                    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="#94A3B8" strokeWidth={2}>
                      <circle cx="11" cy="11" r="7" />
                      <path d="m20 20-3.5-3.5" />
                    </svg>
                  </span>
                  <span className="flex-1 min-w-0 truncate text-[13.5px] font-medium" style={{ color: "var(--text, #020618)" }}>
                    {title}
                  </span>
                </button>
              ))
            )}
          </div>
        )}
      </div>
    </div>
  );
}

/**
 * Filter icon + dropdown for the /search page's Ажил tab — salary, job level,
 * industry and employment type, the same four filters Home.jsx keeps as
 * mobile bottom sheets, collapsed into one button here since /search has no
 * room (and no desktop sidebar) for four separate pills. State lives in
 * SearchFilterContext so Search.jsx can read the resulting apiFilters
 * without this button and that page needing to be the same component.
 */
function SearchFilterButton() {
  const { t } = useTranslation();
  const {
    isOpen, toggle, close,
    salaryRange, setSalaryRange, salaryMinLimit, salaryMaxLimit, salaryStep,
    selectedLevels, toggleLevel,
    selectedIndustries, toggleIndustry,
    selectedEmploymentType, setSelectedEmploymentType,
    jobLevels, industries, referenceLoading,
    getLevelDisplayName, getIndustryDisplayName,
    activeCount, reset,
  } = useSearchFilter();
  const panelRef = React.useRef(null);

  useEffect(() => {
    if (!isOpen) return undefined;
    const onDown = (e) => {
      if (panelRef.current && !panelRef.current.contains(e.target)) close();
    };
    document.addEventListener("mousedown", onDown);
    return () => document.removeEventListener("mousedown", onDown);
  }, [isOpen, close]);

  const formatSalaryLabel = (value) => {
    if (value >= 10000000) return `10${t("salary.million")}+`;
    if (value >= 1000000) return `${(value / 1000000).toFixed(1).replace('.0', '')}${t("salary.million")}`;
    if (value >= 100000) return `${(value / 100000) * 100}${t("salary.thousand")}`;
    return `0 ${t("salary.currency")}`;
  };

  return (
    <div className="relative" ref={panelRef}>
      <button
        onClick={toggle}
        aria-label={t("home.filters", "Шүүлтүүр")}
        className="relative flex items-center justify-center w-10 h-10 rounded-full flex-none"
        style={{ background: "var(--card2, #EEF1F8)", border: "1px solid var(--border, #E2E8F0)" }}
      >
        <FiFilter className="text-[17px] text-[#020618]" />
        {activeCount > 0 && (
          <span className="absolute -top-1 -right-1 min-w-[18px] h-[18px] px-1 rounded-full bg-[#4F6BF6] border-2 border-white flex items-center justify-center text-[10px] font-extrabold text-white leading-none tabular-nums">
            {activeCount}
          </span>
        )}
      </button>

      {isOpen && (
        <div
          className="fixed left-3 right-3 top-[4.25rem] md:absolute md:left-auto md:right-0 md:top-[3.25rem] md:w-[24rem] z-50 rounded-2xl bg-white overflow-hidden"
          style={{ border: "1px solid var(--border2, #CBD5E1)", boxShadow: "0 18px 40px rgba(15,23,42,0.14)" }}
        >
          <div className="flex items-center justify-between px-4 pt-3.5 pb-2 border-b border-[#F1F5F9]">
            <span className="font-bold text-[14px] text-[#020618]">{t("home.filters", "Шүүлтүүр")}</span>
            {activeCount > 0 && (
              <button onClick={reset} className="text-[12px] font-bold text-[#64748B] underline">
                {t("home.reset")}
              </button>
            )}
          </div>

          <div className="max-h-[60vh] overflow-y-auto px-4 py-3 flex flex-col gap-4">
            {/* Salary */}
            <div>
              <p className="text-[12.5px] font-bold text-[#020618] mb-2">{t("home.salary")}</p>
              <div className="flex items-center justify-between mb-2">
                <span className="text-[12px] font-medium text-[#64748B]">{formatSalaryLabel(salaryRange[0])}</span>
                <span className="text-[12px] font-medium text-[#64748B]">{formatSalaryLabel(salaryRange[1])}</span>
              </div>
              <div className="relative h-8 flex items-center">
                <div className="absolute left-0 right-0 h-1.5 bg-[#E2E8F0] rounded-full" />
                <div
                  className="absolute h-1.5 bg-[#020618] rounded-full"
                  style={{
                    left: `${(salaryRange[0] / salaryMaxLimit) * 100}%`,
                    right: `${100 - (salaryRange[1] / salaryMaxLimit) * 100}%`,
                  }}
                />
                <input
                  type="range"
                  min={salaryMinLimit}
                  max={salaryMaxLimit}
                  step={salaryStep}
                  value={salaryRange[0]}
                  onChange={(e) => {
                    const value = Math.min(Number(e.target.value), salaryRange[1] - salaryStep);
                    setSalaryRange([value, salaryRange[1]]);
                  }}
                  className="absolute w-full h-8 appearance-none bg-transparent pointer-events-none [&::-webkit-slider-thumb]:appearance-none [&::-webkit-slider-thumb]:w-5 [&::-webkit-slider-thumb]:h-5 [&::-webkit-slider-thumb]:bg-[#020618] [&::-webkit-slider-thumb]:rounded-full [&::-webkit-slider-thumb]:cursor-pointer [&::-webkit-slider-thumb]:pointer-events-auto [&::-moz-range-thumb]:appearance-none [&::-moz-range-thumb]:w-5 [&::-moz-range-thumb]:h-5 [&::-moz-range-thumb]:bg-[#020618] [&::-moz-range-thumb]:rounded-full [&::-moz-range-thumb]:cursor-pointer [&::-moz-range-thumb]:border-0 [&::-moz-range-thumb]:pointer-events-auto"
                />
                <input
                  type="range"
                  min={salaryMinLimit}
                  max={salaryMaxLimit}
                  step={salaryStep}
                  value={salaryRange[1]}
                  onChange={(e) => {
                    const value = Math.max(Number(e.target.value), salaryRange[0] + salaryStep);
                    setSalaryRange([salaryRange[0], value]);
                  }}
                  className="absolute w-full h-8 appearance-none bg-transparent pointer-events-none [&::-webkit-slider-thumb]:appearance-none [&::-webkit-slider-thumb]:w-5 [&::-webkit-slider-thumb]:h-5 [&::-webkit-slider-thumb]:bg-[#020618] [&::-webkit-slider-thumb]:rounded-full [&::-webkit-slider-thumb]:cursor-pointer [&::-webkit-slider-thumb]:pointer-events-auto [&::-moz-range-thumb]:appearance-none [&::-moz-range-thumb]:w-5 [&::-moz-range-thumb]:h-5 [&::-moz-range-thumb]:bg-[#020618] [&::-moz-range-thumb]:rounded-full [&::-moz-range-thumb]:cursor-pointer [&::-moz-range-thumb]:border-0 [&::-moz-range-thumb]:pointer-events-auto"
                />
              </div>
            </div>

            {/* Employment type */}
            <div>
              <p className="text-[12.5px] font-bold text-[#020618] mb-2">{t("job.employmentType", "Ажлын төрөл")}</p>
              <div className="flex gap-2">
                {[
                  { value: "FULL_TIME", label: t("job.fullTime") },
                  { value: "PART_TIME", label: t("job.partTime") },
                ].map(({ value, label }) => {
                  const isSelected = selectedEmploymentType === value;
                  return (
                    <button
                      key={value}
                      onClick={() => setSelectedEmploymentType(isSelected ? "" : value)}
                      className="flex-1 rounded-[10px] px-2.5 py-2 text-[12.5px] font-bold border-[1.5px] transition-colors"
                      style={{
                        borderColor: isSelected ? "#4F6BF6" : "#E2E8F0",
                        color: isSelected ? "#4F6BF6" : "#64748B",
                        background: isSelected ? "#EEF1FF" : "#fff",
                      }}
                    >
                      {label}
                    </button>
                  );
                })}
              </div>
            </div>

            {/* Job level */}
            <div>
              <p className="text-[12.5px] font-bold text-[#020618] mb-1.5">{t("home.jobLevel")}</p>
              <div className="max-h-[9.5rem] overflow-y-auto flex flex-col">
                {referenceLoading && jobLevels.length === 0 ? (
                  <p className="text-[12px] text-[#94A3B8] py-2">{t("home.loading", "Уншиж байна...")}</p>
                ) : (
                  jobLevels.map((level) => {
                    const isSelected = selectedLevels.includes(level.id);
                    return (
                      <button
                        key={level.id}
                        onClick={() => toggleLevel(level.id)}
                        className="w-full flex items-center justify-between py-2 border-b border-[#F8FAFC] text-left"
                      >
                        <span className={`text-[13px] ${isSelected ? 'text-[#020618] font-bold' : 'text-[#334155]'}`}>
                          {getLevelDisplayName(level)}
                        </span>
                        {isSelected && <FiCheck className="text-[#4F6BF6] text-base flex-none" />}
                      </button>
                    );
                  })
                )}
              </div>
            </div>

            {/* Industry */}
            <div>
              <p className="text-[12.5px] font-bold text-[#020618] mb-1.5">{t("home.industry")}</p>
              <div className="max-h-[9.5rem] overflow-y-auto flex flex-col">
                {referenceLoading && industries.length === 0 ? (
                  <p className="text-[12px] text-[#94A3B8] py-2">{t("home.loading", "Уншиж байна...")}</p>
                ) : (
                  industries.map((industry) => {
                    const isSelected = selectedIndustries.includes(industry.id);
                    return (
                      <button
                        key={industry.id}
                        onClick={() => toggleIndustry(industry.id)}
                        className="w-full flex items-center justify-between py-2 border-b border-[#F8FAFC] text-left"
                      >
                        <span className={`text-[13px] ${isSelected ? 'text-[#020618] font-bold' : 'text-[#334155]'}`}>
                          {getIndustryDisplayName(industry)}
                        </span>
                        {isSelected && <FiCheck className="text-[#4F6BF6] text-base flex-none" />}
                      </button>
                    );
                  })
                )}
              </div>
            </div>
          </div>

          <div className="px-4 py-3 border-t border-[#F1F5F9]">
            <button
              onClick={close}
              className="w-full py-2.5 rounded-xl bg-[#020618] text-white text-[13px] font-bold"
            >
              {t("home.apply", "Хэрэглэх")}
            </button>
          </div>
        </div>
      )}
    </div>
  );
}

const Header = () => {
  const { sidebar, setSidebar } = useSidebar();
  const { unreadCount } = useNotifications();
  const [showNotifications, setShowNotifications] = useState(false);

  /**
   * Desktop opens the panel; mobile goes to the page.
   *
   * Measured at the tap rather than tracked as state: this is a one-off read at the moment
   * of a click, not something the header re-renders on — and a resize between renders would
   * only ever change which of two correct destinations it picked.
   */
  const openNotifications = () => {
    if (window.innerWidth >= 1024) setShowNotifications(true);
    else navigate("/notifications");
  };
  const { isInTokiApp, platform: tokiPlatform, user: tokiUser, getPhoneUserPhotoUrl } = useToki();
  const { isSuperAppUser, isAuthenticated, retryAuth } = useSuperApp();
  const { goToPost, roleChoiceModal } = usePostAction();

  const navigate = useNavigate();
  // The dedicated search page gets a cleaner mobile header — the profile button hides
  // in favor of the page's own back button, rather than crowding two "leave this page"
  // controls next to each other.
  const { pathname } = useLocation();
  const isSearchPage = pathname === '/search';
  /**
   * Where the filter button is worth drawing: a page that shows a job list this can narrow.
   *
   * /jobs joined /search because the two are the same list asked for two ways — one by a
   * typed query, one by browsing — and the filters were reachable from only one of them. The
   * state behind the button is shared (SearchFilterContext), so a narrowing survives the
   * walk between the two pages instead of being set twice.
   */
  const showsJobFilters = isSearchPage || pathname === '/jobs';
  /**
   * The header paints the same surface as the page under it.
   *
   * It is a sibling of the routed content, not a part of it, so a tinted page left the
   * header sitting there white — on a phone, where the header is the top of the screen, that
   * read as the background changing colour halfway down. The tint only exists on a phone,
   * and so does this: DESK_SURFACE_CLASS goes back to white from md up, exactly where the
   * pages do. See utils/pageSurface.
   */
  const surfaceClass = isDeskHeader(pathname) ? DESK_SURFACE_CLASS : "bg-[var(--bg,#fff)]";

  /**
   * The search chrome outlives /search itself.
   *
   * Opening a job or a company from a search result swapped the back button for the logo
   * and put the profile button back — the header rearranged itself under someone who had
   * only tapped a result, and the way back to their results disappeared with it. A detail
   * page reached from search keeps the chrome search gave it, and Back returns to the
   * results rather than to the home feed.
   *
   * State rather than a route test, because the detail routes are reachable from
   * everywhere: /job/:id opened from the home feed is not part of a search, and should
   * keep the logo. A reload drops the flag and falls back to the logo, which is honest —
   * there is no result list behind that entry any more.
   *
   * The CV bank counts too, list and detail alike: on a phone the CV сан tab on /search
   * opens /admin/cv itself (see Search.jsx), and from there a CV. Leaving the list out made
   * the header swap its back button for the logo the moment the tab was tapped.
   */
  const isDetailPage = /^\/(job|company)\//.test(pathname) || /^\/admin\/cv(\/|$)/.test(pathname);
  const [inSearchFlow, setInSearchFlow] = useState(isSearchPage);
  useEffect(() => {
    if (isSearchPage) setInSearchFlow(true);
    else if (!isDetailPage) setInSearchFlow(false);
  }, [isSearchPage, isDetailPage]);
  const searchChrome = isSearchPage || (inSearchFlow && isDetailPage);
  // Already on the profile page: this button navigated to where you already were, so it
  // opens the role switcher instead — the same sidebar the page's own menu button opens
  // (Profile.jsx listens for this event).
  const isProfilePage = pathname === '/user/profile';

  // Get user avatar - prioritize Toki, then phone user from context
  const userAvatar = tokiUser?.profilePictureFullUrl || getPhoneUserPhotoUrl();

  // Зураг ачаалагдахгүй бол fallback дүрс. State-ээр барьсан учир шинэ URL ирэхэд
  // дахин оролддог — өмнө нь DOM дээр шууд display:none тавьдаг байсан тул нэг
  // алдсаны дараа зураг сольсон ч дахиж харагдахгүй байлаа.
  const [avatarFailed, setAvatarFailed] = useState(false);
  useEffect(() => { setAvatarFailed(false); }, [userAvatar]);
  const showAvatar = !!userAvatar && !avatarFailed;
  const { t } = useTranslation();
  const [showLoginModal, setShowLoginModal] = useState(false);
  const [showPostJobModal, setShowPostJobModal] = useState(false);

  const goToProfile = () => {
    if (isProfilePage) {
      window.dispatchEvent(new CustomEvent('role:switchOpen'));
      return;
    }
    navigate("/user/profile");
  };

  const openProfileOrLogin = async () => {
    if (isAuthenticated) {
      goToProfile();
    } else if (isSuperAppUser) {
      const success = await retryAuth();
      if (success) navigate("/user/profile");
    } else {
      setShowLoginModal(true);
    }
  };

  const handleLoginSuccess = () => {
    setShowLoginModal(false);
    navigate("/user/profile");
  };

  // Right padding so Toki's own "? ✕" pill does not cover the header's buttons - mobile
  // only. Not on iOS: there Toki draws the pill inside the 40px band Layout already keeps
  // above the header (seen on an iPhone, Toki, 2026-11-03), so the padding only squeezed
  // the search bar down to "Та я". Android keeps it until its pill is checked the same way.
  const tokiMobileClass = isInTokiApp && tokiPlatform !== "ios" ? "pr-[100px] md:pr-6" : "";

  return (
    <>
      <div
        className={`h-[4rem] flex items-center justify-between py-3 md:ps-2 md:pe-6 pe-2 sticky top-0 z-40 ${surfaceClass} ${tokiMobileClass}`}
      >
        <button
          onClick={() => setSidebar(!sidebar)}
          disabled={sidebar}
          className={`w-[40px] h-[40px] p-2 rounded-full border-[3px] border-transparent hover:border-[#E2E8F0] lg:flex hidden items-center justify-center transition-all duration-300 ${sidebar && "opacity-0"
            }`}
        >
          <RxDoubleArrowRight className="text-[24px]" />
        </button>
        {/* A full "diil" wordmark used to sit here under `lg:hidden md:block hidden` — a
            tablet-only slot that stopped existing when md moved from 768 to 1024 (both
            variants now switch at the same width, so lg:hidden won at every size and it
            never painted). Desktop gets its wordmark from the sidebar instead. */}
        {/* Search swaps the top-left logo for a back button — the page has nowhere else in
            its own chrome for one, and the logo's "go home" action isn't what you want
            mid-search. Stays through a job/company opened from the results (searchChrome). */}
        {searchChrome ? (
          // The header has no start padding on mobile (only `md:ps-2`) — fine for the
          // logo mark, which reads as flush-left by design, but a round button sitting
          // right against the screen edge with nothing else there looks pinched. `ml-2`
          // gives it its own breathing room regardless of that container padding.
          <button
            onClick={() => navigate(-1)}
            aria-label="back"
            className="md:hidden flex items-center justify-center w-10 h-10 rounded-full flex-none ml-2"
            style={{ background: "var(--card2, #EEF1F8)" }}
          >
            <FiArrowLeft className="text-lg" style={{ color: "#020618" }} />
          </button>
        ) : (
          <img
            onClick={() => { window.location.href = "/"; }}
            className={`md:hidden block cursor-pointer header-logo-mobile ${isInTokiApp ? "pl-2" : ""}`}
            src="/img/logo/logoc.svg"
            alt="logo"
          />
        )}
        {/* min-w-0: a flex item defaults to min-width:auto, which floors this wrapper at
            the search pill's min-content width. At narrow widths the bar then refused to
            shrink and pushed the profile button off the right edge — signed-in users hit
            it first, since the notification bell costs another 48px. */}
        <div className="flex items-center gap-2 flex-1 md:flex-none min-w-0 justify-center md:justify-start">
          <ExpandingSearch />
        </div>
        {/* Right-side actions grouped together so they cluster on the right
            (justify-between was spreading them apart across the bar). */}
        <div className="flex items-center gap-2 flex-none">
          {/* Холбогдох — directly in the header rather than buried in the drawer,
              so it's visible without a single extra tap. /contact is where a business
              asks for Diil's intro and price quote — a report icon, not an envelope. */}
          {!searchChrome && (
            <button
              onClick={() => navigate("/contact")}
              aria-label={t('sidebar.contact', 'Холбогдох')}
              className="lg:hidden flex items-center justify-center w-10 h-10 rounded-full flex-none"
              style={{ background: "var(--card2, #EEF1F8)", border: "1px solid var(--border, #E2E8F0)" }}
            >
              <HiOutlineDocumentChartBar className="text-[20px] text-[#020618]" />
            </button>
          )}
          {isAuthenticated ? (
            <>
              {/* Post-job button — shown to logged-in users, same size as the
                  CV button. */}
              <button
                onClick={() => goToPost(() => setShowLoginModal(true), () => setShowPostJobModal(true))}
                className="group h-11 pe-1 ps-6 gap-3 lg:flex hidden items-center bg-[#020618] rounded-[99px] text-[#fff] hover:bg-[#29EAFF] hover:text-[#0F172B]"
              >
                {t("header.postJob", "Ажлын зар нийтлэх")}
                <div className="bg-[#1D293D] h-9 w-9 rounded-full flex items-center justify-center">
                  <FiPlus className="text-xl group-hover:text-[#29EAFF]" />
                </div>
              </button>
              {/* Desktop: a menu rather than a link — role switch (or "Компани
                  бүртгүүлэх"), Профайл, CV, Гарах. See ProfileMenu. */}
              <ProfileMenu
                avatar={userAvatar}
                showAvatar={showAvatar}
                onAvatarError={() => setAvatarFailed(true)}
              />
            </>
          ) : (
            <button
              onClick={openProfileOrLogin}
              className="group h-11 pe-1 ps-6 gap-3 lg:flex hidden items-center bg-[#020618] rounded-[99px] text-[#fff] hover:bg-[#29EAFF] hover:text-[#0F172B]"
            >
              {t("header.login", "Нэвтрэх")}
              <div className="bg-[#1D293D] h-9 w-9 rounded-full flex items-center justify-center">
                <FiArrowUpRight className="text-xl group-hover:text-[#29EAFF]" />
              </div>
            </button>
          )}
          {/* Job-search filters — on the pages that actually show a job list to narrow,
              /search and /jobs (searchChrome also covers a job/company opened from a
              result, which has no list here to filter). Sits left of the bell, same slot
              Home.jsx's own filter pills would occupy if these pages had room for them. */}
          {showsJobFilters && <SearchFilterButton />}
          {/* Мэдэгдэл. Took the place the dark-mode toggle and the language switcher held:
              both of those live in Профайл -> Тохиргоо as well, and neither is something
              anyone reaches for often enough to spend the only two header slots on.
              Signed-out there is nothing to notify about, so it is not rendered at all. */}
          {isAuthenticated && (
            <button
              onClick={openNotifications}
              aria-label="notifications"
              className="relative flex items-center justify-center w-10 h-10 rounded-full flex-none"
              style={{ background: "var(--card2, #EEF1F8)", border: "1px solid var(--border, #E2E8F0)" }}
            >
              <FiBell className="text-[18px] text-[#020618]" />
              {/* The count, not a dot: "something happened" and "eleven things happened" are
                  different enough to act on differently. Reads 99+ past two digits, and sits
                  half outside the circle so it does not crowd the bell.

                  It clears when the list is opened — useNotifications marks everything seen
                  there, and every mount of that hook shares one store (see
                  notificationStore.subscribe), so this badge hears about it. */}
              {unreadCount > 0 && (
                <span className="absolute -top-1 -right-1 min-w-[18px] h-[18px] px-1 rounded-full bg-[#EF4444] border-2 border-white flex items-center justify-center text-[10px] font-extrabold text-white leading-none tabular-nums">
                  {unreadCount > 99 ? "99+" : unreadCount}
                </span>
              )}
            </button>
          )}
          {/* Mobile profile button. Signed in, it opens the same menu as the desktop avatar
              (ProfileMenu); signed out, it is the way to log in. Hidden in the search flow,
              which has its own back button in the same corner of attention. */}
          {!searchChrome && (isAuthenticated ? (
            <ProfileMenu
              mobile
              avatar={userAvatar}
              showAvatar={showAvatar}
              onAvatarError={() => setAvatarFailed(true)}
            />
          ) : (
          <button
            onClick={openProfileOrLogin}
            className="lg:hidden flex items-center justify-center w-10 h-10 rounded-full overflow-hidden"
          >
            <div className="w-full h-full bg-[#FFE83E] rounded-full flex items-center justify-center">
              <FiUser className="text-lg text-[#020618]" />
            </div>
          </button>
          ))}
        </div>
      </div>

      {/* Unified Login Modal - never shown for super app users */}
      {!isSuperAppUser && (
        <LoginModal
          isOpen={showLoginModal}
          trigger="header"
          onClose={() => setShowLoginModal(false)}
          onSuccess={handleLoginSuccess}
        />
      )}

      {/* Desktop-only popup for posting a job — mobile still navigates to
          the full /employer/post page (see usePostAction). */}
      <PostJobModal isOpen={showPostJobModal} onClose={() => setShowPostJobModal(false)} />

      {/* Desktop's notification drawer. Portalled to the body from inside, so it is not
          clipped by the header's own stacking context. */}
      <NotificationPanel isOpen={showNotifications} onClose={() => setShowNotifications(false)} />
      {roleChoiceModal}
    </>
  );
};

export default Header;
